/**
 * P103/P104/P107 spatial foundation - NONLIVE geometry qualification slice.
 *
 * Tile-EDGE fence segments + gates + flood-fill pens on a parcel tile
 * substrate. Pure geometry: no pricing, no commands, no UI. The grid edge
 * length is a PARAMETER (100 m = 1 ha tiles matches the plan's day-one
 * "400 m heavy perimeter fence on 1 hectare" anchor; 10 m matches the plan
 * appendix P1-04 fine grid) so the geometry forks stay open while the
 * algorithm itself is proven. Tile-edge (not tile-center) so two pens can
 * share one wall - the P104 "gate on a shared wall" bar.
 *
 * Plan anchors: "Fence per metre: basic 8; heavy 20; predator-proof 45";
 * appendix P1-04 flood fill: a gate is "a wall to the flood fill, a doorway
 * to animals"; a pocket counts "only when the reserve owns every tile".
 */

export interface GridSpec {
  cols: number;
  rows: number;
  edgeM: number; // meters per tile edge; area per tile = edgeM^2 / 10000 ha
}

/** 3A: owner-approved fine drawing grid (2026-10-23, WhatsApp 12:11:40 batch
 *  confirmation: "10-metre grid choice ... landed"). 10 m cells = 100 m2 =
 *  0.01 ha per tile. */
export const DRAWING_GRID_EDGE_M = 10;

/** 3A geometry choice (documented, flagged): parcels carry hectares but no
 *  dimensions, so the drawing grid is a SQUARE-ISH rectangle covering the
 *  parcel area at 10 m cells - tiles = round(ha x 100), cols = ceil(sqrt),
 *  rows = ceil(tiles/cols). cols x rows can overshoot the parcel area by less
 *  than one row; the pen area budget (budgetFreeHa) keeps enclosed area
 *  honest against actual parcel hectares. Same approximation family as
 *  landtools' square-parcel perimeter, one level finer. */
export function gridSpecForHectares(hectares: number): GridSpec {
  const tiles = Math.max(1, Math.round(hectares * 100));
  const cols = Math.max(1, Math.ceil(Math.sqrt(tiles)));
  const rows = Math.max(1, Math.ceil(tiles / cols));
  return { cols, rows, edgeM: DRAWING_GRID_EDGE_M };
}

/** Canonical edge key: 'x,y,N' = edge north of cell (x,y); 'x,y,W' = edge west of cell (x,y). */
export type EdgeKey = string;
export function edgeNorth(x: number, y: number): EdgeKey { return `${String(x)},${String(y)},N`; }
export function edgeWest(x: number, y: number): EdgeKey { return `${String(x)},${String(y)},W`; }

/** The two cells an edge separates (in-grid tile ids only; border edges yield one). */
export function cellsAcross(key: EdgeKey, cols: number, rows: number): number[] {
  const [xs, ys, dir] = key.split(',');
  const x = Number(xs); const y = Number(ys);
  const out: number[] = [];
  if (dir === 'N') {
    if (y < rows) out.push(y * cols + x);
    if (y > 0) out.push((y - 1) * cols + x);
  } else {
    if (x < cols) out.push(y * cols + x);
    if (x > 0) out.push(y * cols + (x - 1));
  }
  return out;
}

export interface FenceLayout {
  segments: ReadonlySet<EdgeKey>;
  gates: ReadonlySet<EdgeKey>; // subset of segments: a wall to the flood fill, a doorway to animals
  /** 4A: per-edge fence grade (1 basic / 2 heavy / 3 predator-proof). Absent =
   *  grade-less geometry fixture (pure qual tests); commands always set it. */
  grades?: ReadonlyMap<EdgeKey, number>;
}

export function addSegment(layout: FenceLayout, key: EdgeKey, gate = false, grade?: number): FenceLayout {
  const segments = new Set(layout.segments);
  segments.add(key);
  const gates = new Set(layout.gates);
  if (gate) gates.add(key);
  const grades = new Map(layout.grades ?? []);
  if (grade !== undefined) grades.set(key, grade);
  return { segments, gates, grades };
}

export function removeSegment(layout: FenceLayout, key: EdgeKey): FenceLayout {
  const segments = new Set(layout.segments);
  segments.delete(key);
  const gates = new Set(layout.gates);
  gates.delete(key);
  const grades = new Map(layout.grades ?? []);
  grades.delete(key);
  return { segments, gates, grades };
}

/** Grade on an edge, 0 when the segment carries none (geometry fixtures). */
export function edgeGrade(layout: FenceLayout, key: EdgeKey): number {
  return layout.grades?.get(key) ?? 0;
}

export interface Pen {
  cells: number[];        // tile ids (y * cols + x), sorted ascending
  areaTiles: number;
  gateEdges: EdgeKey[];   // gates on this pen's boundary; a gate on a shared wall appears in BOTH pens' lists
  perimeterEdges: number; // boundary edges (pen cell against outside, unowned, or another pocket)
}

const DIRS: ReadonlyArray<{ edge: (x: number, y: number) => EdgeKey; dx: number; dy: number }> = [
  { edge: edgeNorth, dx: 0, dy: -1 },
  { edge: edgeWest, dx: -1, dy: 0 },
  { edge: (x, y) => edgeNorth(x, y + 1), dx: 0, dy: 1 },
  { edge: (x, y) => edgeWest(x + 1, y), dx: 1, dy: 0 },
];

/** Flood fill from the map border; closed pockets where every tile is owned become pens. */
export function findPens(grid: GridSpec, layout: FenceLayout, isOwned: (x: number, y: number) => boolean): Pen[] {
  const { cols, rows } = grid;
  const outside = new Int8Array(cols * rows);
  const queue: number[] = [];
  const mark = (id: number): void => { if (outside[id] === 0) { outside[id] = 1; queue.push(id); } };
  // Virtual outside: a border cell leaks out through any unfenced border edge.
  for (let x = 0; x < cols; x++) {
    if (!layout.segments.has(edgeNorth(x, 0))) mark(x);
    if (!layout.segments.has(edgeNorth(x, rows))) mark((rows - 1) * cols + x);
  }
  for (let y = 0; y < rows; y++) {
    if (!layout.segments.has(edgeWest(0, y))) mark(y * cols);
    if (!layout.segments.has(edgeWest(cols, y))) mark(y * cols + cols - 1);
  }
  while (queue.length > 0) {
    const id = queue.pop() as number;
    const x = id % cols; const y = Math.floor(id / cols);
    for (const d of DIRS) {
      const nx = x + d.dx; const ny = y + d.dy;
      if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
      if (layout.segments.has(d.edge(x, y))) continue;
      mark(ny * cols + nx);
    }
  }
  const pens: Pen[] = [];
  const seen = new Int8Array(cols * rows);
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const start = y * cols + x;
      if (outside[start] !== 0 || seen[start] !== 0) continue;
      const pocket: number[] = [];
      const inner: number[] = [start];
      seen[start] = 1;
      while (inner.length > 0) {
        const id = inner.pop() as number;
        pocket.push(id);
        const cx = id % cols; const cy = Math.floor(id / cols);
        for (const d of DIRS) {
          const nx = cx + d.dx; const ny = cy + d.dy;
          if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
          const nid = ny * cols + nx;
          if (outside[nid] !== 0 || seen[nid] !== 0) continue;
          if (layout.segments.has(d.edge(cx, cy))) continue;
          seen[nid] = 1;
          inner.push(nid);
        }
      }
      pocket.sort((a, b) => a - b);
      if (!pocket.every((id) => isOwned(id % cols, Math.floor(id / cols)))) continue; // plan: pocket counts only when the reserve owns every tile
      const pocketSet = new Set(pocket);
      const gates: EdgeKey[] = [];
      let perimeter = 0;
      for (const id of pocket) {
        const cx = id % cols; const cy = Math.floor(id / cols);
        for (const d of DIRS) {
          const nx = cx + d.dx; const ny = cy + d.dy;
          const acrossInside = nx >= 0 && ny >= 0 && nx < cols && ny < rows && pocketSet.has(ny * cols + nx);
          if (acrossInside) continue;
          perimeter += 1;
          const key = d.edge(cx, cy);
          if (layout.gates.has(key) && !gates.includes(key)) gates.push(key);
        }
      }
      pens.push({ cells: pocket, areaTiles: pocket.length, gateEdges: gates.sort(), perimeterEdges: perimeter });
    }
  }
  return pens;
}

export function areaHa(pen: Pen, grid: GridSpec): number {
  return (pen.areaTiles * grid.edgeM * grid.edgeM) / 10000;
}

export function perimeterM(pen: Pen, grid: GridSpec): number {
  return pen.perimeterEdges * grid.edgeM;
}

/* Serde-native state slice (arrays, not Sets - saves are positional). */

export interface ParcelLayoutSer {
  cols: number;
  rows: number;
  edgeM: number;
  segments: string[];
  gates: string[];
  /** 4A: grade per segment (parallel to segments; 0 = grade-less fixture). */
  grades?: number[];
}

export interface SpatialSlice {
  layouts: Record<string, ParcelLayoutSer>; // parcelId -> fence layout on that parcel's grid
  penCells: Record<string, number[]>;       // enclosureId -> pen tile ids; legacy (shape-less) enclosures absent
}

export function freshSpatial(): SpatialSlice {
  return { layouts: {}, penCells: {} };
}

export function layoutFromSer(ser: ParcelLayoutSer): FenceLayout {
  const grades = new Map<EdgeKey, number>();
  ser.segments.forEach((key, i) => {
    const g = ser.grades?.[i] ?? 0;
    if (g > 0) grades.set(key, g);
  });
  return { segments: new Set(ser.segments), gates: new Set(ser.gates), grades };
}

export function layoutToSer(cols: number, rows: number, edgeM: number, layout: FenceLayout): ParcelLayoutSer {
  const segments = [...layout.segments].sort();
  return { cols, rows, edgeM, segments, gates: [...layout.gates].sort(), grades: segments.map((k) => layout.grades?.get(k) ?? 0) };
}

/**
 * Shared parcel area budget. Legacy shape-less enclosures reserve their
 * (possibly fractional) areaHa; spatial pens consume whole tiles. The free
 * remainder never strands more than a fraction of one tile: a tile is
 * paintable only while the full tile still fits the budget, and any
 * sub-tile remainder simply stays in the free pool. No sliver becomes
 * permanently unpaintable land - the legacy pen keeps using its exact area.
 */
export function budgetFreeHa(parcelHectares: number, tileHa: number, spatialPenTiles: number[], legacyAreaHa: number[]): number {
  const used = spatialPenTiles.reduce((a, t) => a + t * tileHa, 0) + legacyAreaHa.reduce((a, x) => a + x, 0);
  return parcelHectares - used;
}

/** True when a pen of tileCount whole tiles fits the current budget. */
export function penFitsBudget(parcelHectares: number, tileHa: number, spatialPenTiles: number[], legacyAreaHa: number[], tileCount: number): boolean {
  return tileCount * tileHa <= budgetFreeHa(parcelHectares, tileHa, spatialPenTiles, legacyAreaHa) + 1e-9;
}

/** Boundary edge keys of a closed pen: fenced edges with a non-pen or off-grid
 *  neighbor. Pure geometry over the serialized layout - shared by the sim
 *  (claim/re-grade) and the UI (re-grade consequence preview). */
export function penBoundaryEdges(l: ParcelLayoutSer, pen: Pen): string[] {
  const cells = new Set(pen.cells);
  const out: string[] = [];
  for (const id of pen.cells) {
    const x = id % l.cols; const y = Math.floor(id / l.cols);
    const edges: Array<[string, number, number]> = [
      [`${String(x)},${String(y)},N`, x, y - 1],
      [`${String(x)},${String(y)},W`, x - 1, y],
      [`${String(x)},${String(y + 1)},N`, x, y + 1],
      [`${String(x + 1)},${String(y)},W`, x + 1, y],
    ];
    for (const [key, ax, ay] of edges) {
      const across = ax >= 0 && ay >= 0 && ax < l.cols && ay < l.rows ? ay * l.cols + ax : -1;
      if (across >= 0 && cells.has(across)) continue; // interior edge
      if (l.segments.includes(key)) out.push(key);
    }
  }
  return out;
}
